import React, { memo, useMemo } from 'react';

import AddToPhotosOutlined from '@mui/icons-material/AddToPhotosOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { Box, Link, Typography, useTheme } from '@/shared/ui';

import type { CatalogItem } from '@/entities/catalog-item';
import type { SymbolKey } from '@/entities/symbol';
import FactsheetDialogButton from '@/features/Factsheet/components/FactsheetDialogButton';
import Tooltip from '@/shared/ui/primitives/Tooltip';
import { formatNumberWithPrecision } from '@/utils/formatNumberWithPrecision';
import { getDiffColor } from '@/utils/getDiffColor';
import { ADDITIONAL_ITEM } from '../../../../constants';
import { SymbolCheckbox } from '../SymbolCheckbox/SymbolCheckbox';
import useCatalogTableCell from '../useCatalogTableCell';

import type { Cell } from '@tanstack/react-table';

const factsheetCellSx = { display: 'flex', justifyContent: 'center', width: '100%' };

const AdditionalItem = () => {
  return (
    <div style={{ width: '100%' }}>
      <Tooltip
        title={
          <>
            <Typography sx={{ fontSize: 14 }}>This GX code has additional items.</Typography>
            <Typography sx={{ fontSize: 14 }}>Check Display settings to view.</Typography>
          </>
        }
      >
        <span
          style={{
            display: 'inline-block',
            maxWidth: '100%'
          }}
        >
          <AddToPhotosOutlined sx={{ fontSize: '16px' }} />
        </span>
      </Tooltip>
    </div>
  );
};

const SpotCatalogCell = ({
  cell,
  isRestrictedData,
  symbol,
  isInDashboard
}: {
  cell: Cell<CatalogItem, unknown>;
  isRestrictedData: boolean;
  symbol?: SymbolKey;
  isInDashboard: boolean;
}) => {
  const columnId = cell.column.id;
  const cellValue = cell.getValue() as string;

  const { palette } = useTheme();
  const { renderContent, defaultStyle } = useCatalogTableCell(columnId, cell);

  const restrictedTitle = useMemo(
    () => (
      <>
        You don't have access to this.{' '}
        <Link
          color='gx.text'
          href='https://www.general-index.com/contact-us'
          rel='noopener noreferrer'
          sx={{ fontWeight: 700 }}
          target='_blank'
        >
          Request access
        </Link>
      </>
    ),
    []
  );

  const returnCellColor = (isChangeColumn?: boolean, value?: string) => {
    if (isRestrictedData) return palette.table.infoRowText;
    if (isChangeColumn) return getDiffColor(Number(value));
    return 'inherit';
  };
  const spotStyle = {
    ...defaultStyle,
    justifyContent: ['Last', 'Change'].includes(columnId) ? 'flex-end' : 'flex-start',
    color: returnCellColor(columnId === 'Change', cellValue)
  };
  const renderCellContent = () => {
    switch (columnId) {
      case ADDITIONAL_ITEM: {
        if (!cell.row.original[ADDITIONAL_ITEM]) return <span />;
        return <AdditionalItem />;
      }
      case 'Symbol': {
        if (!symbol) return <span />;
        return (
          <SymbolCheckbox
            disabled={isRestrictedData}
            symbol={symbol}
          />
        );
      }
      case 'Index':
        return <span>{cellValue === 'Y' ? 'Index' : 'High, Mid, Low'}</span>;
      case 'Last':
      case 'Change':
        if (isRestrictedData) {
          return (
            <Tooltip title={restrictedTitle}>
              <LockOutlined
                color='inherit'
                sx={{ fontSize: '16px' }}
              />
            </Tooltip>
          );
        }
        return <span>{formatNumberWithPrecision(cellValue)}</span>;
      case 'Factsheet':
        return (
          <Box sx={factsheetCellSx}>
            <FactsheetDialogButton
              code={cell.row.getValue('Code')}
              mode={isInDashboard ? 'newTab' : 'modal'}
            />
          </Box>
        );
      default:
        return <>{renderContent()}</>;
    }
  };

  return (
    <div
      key={cell.id + columnId}
      style={spotStyle}
    >
      {renderCellContent()}
    </div>
  );
};

export default memo(SpotCatalogCell);
